<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <div class="cr-page">
          <div class="cr-head">
            <div>
              <h2>Competition results — {{ adminYear }}</h2>
              <p class="muted">
                Awards on dancer <strong>IABF history</strong>.
                Pick the person, then the piece and placement.
              </p>
              <p v-if="!isArchiveYear" class="cr-live-note">
                For {{ adminYear }}, enter 1st / 2nd / 3rd and score on
                <router-link to="/iabf/admin/competition">Competition</router-link>.
                This page is the archive-style editor — extra award rows (Judges' Choice, named awards) still go here.
              </p>
            </div>
            <div class="cr-head-actions">
              <v-btn small outlined :loading="loading" @click="reload">Reload</v-btn>
              <v-btn small color="primary" @click="addRow">+ Add row</v-btn>
              <v-btn
                small
                color="success"
                :loading="saving"
                :disabled="!dirty"
                @click="save"
              >
                Save changes
              </v-btn>
            </div>
          </div>

          <div class="cr-summary">
            <div class="cr-stat">
              <div class="cr-stat-num">{{ rows.length }}</div>
              <div class="cr-stat-lab">Rows</div>
            </div>
            <div class="cr-stat">
              <div class="cr-stat-num">{{ withPersonCount }}</div>
              <div class="cr-stat-lab">With person</div>
            </div>
          </div>

          <p v-if="statusMsg" class="cr-status" :class="{ err: statusIsError }">{{ statusMsg }}</p>

          <div v-if="loading && !rows.length" class="muted">Loading…</div>

          <div v-else class="cr-table-wrap">
            <table class="cr-table">
              <thead>
                <tr>
                  <th style="width:2.5rem">#</th>
                  <th style="min-width:12rem">Dancer</th>
                  <th>Piece</th>
                  <th>Division</th>
                  <th>Placement / award</th>
                  <th>Notes</th>
                  <th style="width:2.5rem"></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(r, i) in rows"
                  :key="r._key"
                  :class="rowClass(r)"
                >
                  <td class="muted">{{ i + 1 }}</td>
                  <td>
                    <div v-if="r.personId" class="cr-cast">
                      <span class="cr-chip">
                        {{ dancerLabel(r) }}
                        <button type="button" class="cr-chip-x" @click="clearPerson(r)">×</button>
                      </span>
                    </div>
                    <AdminPersonPicker
                      v-else
                      :people="people"
                      @select="setPerson(r, $event)"
                    />
                  </td>
                  <td><input v-model="r.pieceTitle" class="cr-input" @input="markDirty"></td>
                  <td><input v-model="r.division" class="cr-input" @input="markDirty"></td>
                  <td><input v-model="r.placement" class="cr-input" @input="markDirty"></td>
                  <td><input v-model="r.notes" class="cr-input" @input="markDirty"></td>
                  <td>
                    <button type="button" class="cr-del" title="Remove row" @click="removeRow(i)">✕</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="!rows.length" class="muted cr-empty">
              No results for {{ adminYear }} yet. Click <strong>+ Add row</strong> to create one.
            </p>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { isArchiveYear as yearIsArchive } from '@/config/iabf-event'
import AdminPersonPicker from '@/components/iabf/AdminPersonPicker.vue'

let keySeq = 0
function emptyRow () {
  return {
    _key: 'n' + (++keySeq),
    personId: '',
    email: '',
    firstName: '',
    lastName: '',
    pieceTitle: '',
    division: '',
    placement: '',
    notes: '',
    pieceId: '',
    dancerId: '',
    score: '',
    source: ''
  }
}

export default {
  name: 'CompetitionResultsAdmin',
  components: { AdminPersonPicker },
  data () {
    return {
      loading: false,
      saving: false,
      rows: [],
      people: [],
      dirty: false,
      statusMsg: '',
      statusIsError: false
    }
  },
  computed: {
    adminYear () {
      return this.$store.getters.adminFestivalYear
    },
    isArchiveYear () {
      return yearIsArchive(this.adminYear)
    },
    withPersonCount () {
      return this.rows.filter(r => r.personId).length
    }
  },
  watch: {
    adminYear: {
      immediate: true,
      handler () {
        this.reload()
      }
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
  },
  methods: {
    rowFromSaved (r) {
      const person = this.resolvePerson(r)
      return {
        _key: 's' + (++keySeq),
        personId: person ? person.id : (r.personId || ''),
        email: person ? person.email : (r.email || ''),
        firstName: person ? person.firstName : (r.firstName || ''),
        lastName: person ? person.lastName : (r.lastName || ''),
        pieceTitle: r.pieceTitle || '',
        division: r.division || '',
        placement: r.placement || '',
        notes: r.notes || '',
        pieceId: r.pieceId || '',
        dancerId: r.dancerId || '',
        score: r.score || '',
        source: r.source || ''
      }
    },
    resolvePerson (r) {
      if (r.personId) {
        return this.people.find(p => p.id === r.personId) || null
      }
      const email = String(r.email || '').toLowerCase()
      if (email) {
        return this.people.find(p => String(p.email || '').toLowerCase() === email) || null
      }
      return null
    },
    dancerLabel (r) {
      const name = [r.firstName, r.lastName].filter(Boolean).join(' ')
      return name || r.email || 'Dancer'
    },
    setPerson (row, person) {
      row.personId = person.id
      row.firstName = person.firstName
      row.lastName = person.lastName
      row.email = person.email
      this.markDirty()
    },
    clearPerson (row) {
      row.personId = ''
      row.firstName = ''
      row.lastName = ''
      row.email = ''
      this.markDirty()
    },
    markDirty () {
      this.dirty = true
      this.statusMsg = ''
    },
    addRow () {
      this.rows.push(emptyRow())
      this.markDirty()
    },
    removeRow (i) {
      this.rows.splice(i, 1)
      this.markDirty()
    },
    rowClass (r) {
      return r.personId ? 'is-matched' : ''
    },
    async reload () {
      this.loading = true
      this.statusMsg = ''
      this.dirty = false
      try {
        this.people = (await this.$store.dispatch('loadIabfPeopleDirectory')) || []
        const saved = await this.$store.dispatch(
          'loadCompetitionResultsForYear',
          this.adminYear
        )
        this.rows = (saved || []).map(r => this.rowFromSaved(r))
      } catch (e) {
        this.statusMsg = 'Could not load results.'
        this.statusIsError = true
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async save () {
      this.saving = true
      this.statusMsg = ''
      try {
        const payload = this.rows.map(r => ({
          personId: r.personId,
          email: r.email,
          firstName: r.firstName,
          lastName: r.lastName,
          pieceTitle: r.pieceTitle,
          division: r.division,
          placement: r.placement,
          notes: r.notes,
          pieceId: r.pieceId,
          dancerId: r.dancerId,
          score: r.score,
          source: r.source
        }))
        const saved = await this.$store.dispatch('saveCompetitionResultsForYear', {
          year: this.adminYear,
          results: payload
        })
        this.rows = (saved || []).map(r => this.rowFromSaved(r))
        this.dirty = false
        this.statusMsg = `Saved ${this.rows.length} result(s) for ${this.adminYear}.`
        this.statusIsError = false
      } catch (e) {
        console.warn(e)
        this.statusMsg = 'Save failed. Check admin permissions and try again.'
        this.statusIsError = true
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.cr-page {
  max-width: 1200px;
  padding-bottom: 48px;
}
.cr-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.cr-head h2 {
  margin: 0 0 6px;
}
.cr-head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.muted {
  color: #666;
  margin: 0;
}
.cr-live-note {
  margin: 8px 0 0;
  padding: 8px 10px;
  background: #f3f8f8;
  border: 1px solid #cfe3e2;
  border-radius: 8px;
  color: #1a4d4c;
  font-size: 13px;
}
.cr-live-note a {
  color: #267E7D;
  font-weight: 600;
}
.cr-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0;
}
.cr-stat {
  background: #f7f7f8;
  border: 1px solid #e5e5e8;
  border-radius: 12px;
  padding: 12px 18px;
  min-width: 100px;
}
.cr-stat-num {
  font-size: 22px;
  font-weight: 700;
}
.cr-stat-lab {
  font-size: 12px;
  color: #777;
}
.cr-status {
  margin: 0 0 12px;
  font-size: 14px;
  color: #1a7a3c;
}
.cr-status.err {
  color: #a05a00;
}
.cr-table-wrap {
  overflow-x: auto;
  background: #fff;
  border: 1px solid #e5e5e8;
  border-radius: 12px;
}
.cr-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.cr-table th,
.cr-table td {
  text-align: left;
  padding: 8px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.cr-table th {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #777;
  background: #fafafa;
  white-space: nowrap;
}
.cr-table tr.is-ambiguous {
  background: #fffaf0;
}
.cr-table tr.is-matched {
  background: #f6fbf7;
}
.cr-table tr.is-unmatched {
  background: #fafafa;
}
.cr-input {
  width: 100%;
  min-width: 5rem;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font: inherit;
  background: #fff;
}
.cr-input:focus {
  outline: none;
  border-color: #388b9d;
  box-shadow: 0 0 0 2px rgba(56, 139, 157, 0.2);
}
.cr-match-cell {
  white-space: nowrap;
}
.cr-badge {
  display: inline-block;
  min-width: 1.25rem;
  text-align: center;
  font-weight: 700;
  margin-right: 4px;
}
.cr-badge--ok { color: #1a7a3c; }
.cr-badge--warn { color: #a05a00; }
.cr-badge--miss { color: #999; }
.cr-match-btn {
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  cursor: pointer;
  color: #267E7D;
  font-weight: 600;
}
.cr-match-btn:hover {
  background: #def0ef;
  border-color: #267E7D;
}
.cr-del {
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 6px;
}
.cr-del:hover {
  background: #fdecea;
  color: #b00020;
}
.cr-cast {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.cr-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef6f6;
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 12px;
}
.cr-chip-x {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 0 0 2px;
}
.cr-empty {
  padding: 16px 18px;
}
</style>
